import { useState } from 'react';
import { useNavigate } from 'react-router';
import { completePasswordChange, login, verifyTotpLogin, type CurrentUser, type LoginStepUpRequired, type PasswordChangeRequired } from '../auth/auth.api';
import { useCurrentUser } from '../auth/CurrentUserProvider';
import { useToast } from '../lib/use-toast';

export type LoginStep = 'password' | 'code' | 'newPassword';

interface PasswordFormValues {
  email: string;
  password: string;
}

/** The next step for whatever a correct password (or a completed forced change) just produced — shared by submitPassword and submitNewPassword. */
function advanceFromOutcome(
  result: CurrentUser | LoginStepUpRequired | PasswordChangeRequired,
  callbacks: { toNewPassword: () => void; toCode: () => void; onSignedIn: (user: CurrentUser) => void },
) {
  if ('passwordChangeRequired' in result) return callbacks.toNewPassword();
  if ('requiresTotp' in result) return callbacks.toCode();
  callbacks.onSignedIn(result);
}

/** A second step only when the account has TOTP enabled or owes a forced password change — otherwise password alone signs in, exactly as before. */
export function useLoginPage() {
  const navigate = useNavigate();
  const { setUser } = useCurrentUser();
  const toast = useToast();
  const [step, setStep] = useState<LoginStep>('password');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  function onSignedIn(user: CurrentUser) {
    setUser(user);
    navigate('/', { replace: true });
    toast.success(`Welcome back, ${user.name.split(' ')[0]}`);
  }

  const advanceCallbacks = { toNewPassword: () => setStep('newPassword'), toCode: () => setStep('code'), onSignedIn };

  async function submitPassword(values: PasswordFormValues) {
    setError(null);
    setSubmitting(true);
    try {
      advanceFromOutcome(await login(values), advanceCallbacks);
    } catch {
      // Mirrors the backend's non-disclosure: one generic message for wrong password, unknown email, or a locked-out account.
      setError('Incorrect email or password.');
    } finally {
      setSubmitting(false);
    }
  }

  async function submitCode(code: string) {
    setError(null);
    setSubmitting(true);
    try {
      onSignedIn(await verifyTotpLogin(code));
    } catch {
      setError('Incorrect code. Try again, or use a recovery code.');
    } finally {
      setSubmitting(false);
    }
  }

  async function submitNewPassword(password: string) {
    setError(null);
    setSubmitting(true);
    try {
      advanceFromOutcome(await completePasswordChange(password), advanceCallbacks);
    } catch {
      setError("Couldn't set that password. Try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return { step, error, submitting, submitPassword, submitCode, submitNewPassword };
}
